import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { TrendingUp, ChevronDown, ChevronRight, BarChart3, Table as TableIcon, AlertTriangle, CheckCircle2, CircleDot } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { computeCashflowForecast } from '../../../lib/cashflowForecast'
import { cn } from '../../../lib/utils'

function fmt(n) {
  if (n == null || isNaN(n)) return '—'
  const v = Number(n)
  return Math.round(v).toLocaleString('en-US')
}

const RISK_BADGE = {
  safe:     { Icon: CheckCircle2, classes: 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/30' },
  warning:  { Icon: CircleDot,    classes: 'bg-amber-500/10 text-amber-600 ring-amber-500/30' },
  critical: { Icon: AlertTriangle, classes: 'bg-red-500/10 text-red-600 ring-red-500/30' },
}

function MonthRow({ row, expanded, onToggle }) {
  const { t } = useTranslation()
  const { Icon: RiskIcon, classes: riskClasses } = RISK_BADGE[row.risk]
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent transition-colors text-start"
      >
        <span className={cn(
          'inline-flex items-center justify-center w-7 h-7 rounded-full ring-1',
          riskClasses,
        )}>
          <RiskIcon className="w-3.5 h-3.5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-mono text-foreground" dir="ltr">{row.monthKey}</p>
          {row.lastDeductions.length > 0 && (
            <p className="text-[10px] text-muted-foreground truncate" dir="auto">
              {t('finance.forecast.lastDeduction')}: {row.lastDeductions.join(', ')}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end text-xs flex-shrink-0">
          <span className={cn(
            'font-mono font-semibold',
            row.net >= 0 ? 'text-emerald-600' : 'text-red-600'
          )} dir="ltr">
            {row.net >= 0 ? '+' : ''}{fmt(row.net)}
          </span>
          <span className="font-mono text-muted-foreground" dir="ltr">
            = {fmt(row.cumulativeBalance)}
          </span>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-muted-foreground rtl:-scale-x-100 flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 bg-muted/30 border-t border-border/40">
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs pt-2 font-mono" dir="ltr">
            <Detail label={t('finance.forecast.income')} value={row.income} tone="positive" />
            <Detail label={t('finance.forecast.fixed')} value={-row.fixed} tone="negative" />
            <Detail label={t('finance.forecast.variable')} value={-row.variable} tone="negative" />
            <Detail label={t('finance.forecast.installments')} value={-row.installmentsTotal} tone="negative" />
            {row.obligationsTotal > 0 && (
              <Detail label={t('finance.forecast.obligations')} value={-row.obligationsTotal} tone="negative" />
            )}
          </dl>
          {(row.installmentItems.length > 0 || row.obligationItems.length > 0) && (
            <ul className="mt-2 text-[10px] text-muted-foreground space-y-0.5" dir="auto">
              {row.installmentItems.map((x) => (
                <li key={`i-${x.id}`} className="flex justify-between gap-2 font-mono">
                  <span className="truncate">{x.label}</span>
                  <span dir="ltr">-{fmt(x.amount)}</span>
                </li>
              ))}
              {row.obligationItems.map((x) => (
                <li key={`o-${x.id}`} className="flex justify-between gap-2 font-mono">
                  <span className="truncate">{x.label}</span>
                  <span dir="ltr">-{fmt(x.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  )
}

function Detail({ label, value, tone }) {
  const toneClass = tone === 'positive' ? 'text-emerald-600' : tone === 'negative' ? 'text-red-600' : 'text-foreground'
  return (
    <>
      <dt className="text-muted-foreground" dir="auto">{label}</dt>
      <dd className={cn('text-end', toneClass)}>{fmt(value)}</dd>
    </>
  )
}

export function CashflowForecast({
  currentBalance,
  incomeSources,
  fixedExpenses,
  variableEstimate,
  installments,
  specialObligations,
}) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(null)
  const [view, setView] = useState('table')

  const rows = useMemo(() => computeCashflowForecast({
    currentBalance,
    incomeSources,
    fixedExpenses,
    variableEstimate,
    installments,
    specialObligations,
    horizonMonths: 12,
  }), [currentBalance, incomeSources, fixedExpenses, variableEstimate, installments, specialObligations])

  const chartData = useMemo(
    () => rows.map((r) => ({
      month: r.monthKey.slice(5),
      balance: Math.round(r.cumulativeBalance),
      net: Math.round(r.net),
    })),
    [rows],
  )

  return (
    <section className="space-y-2 pt-2">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5" dir="auto">
          <TrendingUp className="w-4 h-4 text-app-primary-500" />
          {t('finance.forecast.title')}
        </h2>
        <div className="flex items-center rounded-md border border-input bg-background overflow-hidden">
          <button
            type="button"
            onClick={() => setView('table')}
            aria-pressed={view === 'table'}
            className={cn(
              'px-2 py-1 text-xs flex items-center gap-1 transition-colors',
              view === 'table' ? 'bg-app-primary-500/10 text-app-primary-500' : 'text-muted-foreground hover:bg-accent',
            )}
          >
            <TableIcon className="w-3.5 h-3.5" />
            {t('finance.forecast.viewTable')}
          </button>
          <button
            type="button"
            onClick={() => setView('chart')}
            aria-pressed={view === 'chart'}
            className={cn(
              'px-2 py-1 text-xs flex items-center gap-1 transition-colors',
              view === 'chart' ? 'bg-app-primary-500/10 text-app-primary-500' : 'text-muted-foreground hover:bg-accent',
            )}
          >
            <BarChart3 className="w-3.5 h-3.5" />
            {t('finance.forecast.viewChart')}
          </button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {view === 'chart' ? (
            <div className="p-3">
              <div className="h-56 w-full" dir="ltr">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 8, left: -16, bottom: 0 }}>
                    <defs>
                      <linearGradient id="forecastFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--app-primary-500))" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="hsl(var(--app-primary-500))" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => fmt(v)} />
                    <Tooltip
                      contentStyle={{
                        background: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '0.5rem',
                        fontSize: '0.75rem',
                      }}
                      formatter={(value, name) => [`${fmt(value)} SAR`, t(`finance.forecast.${name}`)]}
                    />
                    <ReferenceLine y={5000} stroke="hsl(var(--muted-foreground))" strokeDasharray="2 4" strokeOpacity={0.4} />
                    <ReferenceLine y={1000} stroke="hsl(0 84% 60%)" strokeDasharray="2 4" strokeOpacity={0.4} />
                    <Area type="monotone" dataKey="balance" stroke="hsl(var(--app-primary-500))" strokeWidth={2} fill="url(#forecastFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <p className="text-[10px] text-muted-foreground text-center mt-1" dir="auto">
                {t('finance.forecast.chartLegend')}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border/40">
              {rows.map((row) => (
                <MonthRow
                  key={row.monthKey}
                  row={row}
                  expanded={expanded === row.monthKey}
                  onToggle={() => setExpanded(expanded === row.monthKey ? null : row.monthKey)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  )
}
